import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowUpCircle, ArrowDownCircle, Plus, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";

interface Movement {
  id: number;
  created_at: string;
  product_name: string;
  type: "entrada" | "saida";
  quantity: number;
  responsible: string;
  product_code: string;
  department_name: string;
  department_sigla: string;
  department_id: number;
}

interface Product {
  id: number;
  name: string;
  code: string;
  category_id?: number;
}

interface Category {
  id: number;
  name: string;
}

interface Department {
  id: number;
  name: string;
  sigla: string;
  quantity?: number;
}

const Movements = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [movements, setMovements] = useState<Movement[]>([]);
  const [stats, setStats] = useState({ entrances: 0, exits: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [availableDepartments, setAvailableDepartments] = useState<Department[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedType, setSelectedType] = useState<"entrada" | "saida">("entrada");
  const [quantity, setQuantity] = useState("");
  const [responsible, setResponsible] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDepartment, setFilterDepartment] = useState<string>("all");
  const [filterType, setFilterType] = useState<"all" | "entrada" | "saida">("all");
  const [filterDateRange, setFilterDateRange] = useState<"all" | "today" | "week" | "month">("all");
  const itemsPerPage = 10;
  const [hasProducts, setHasProducts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchMovements();
    fetchCategories();
    fetchDepartments();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterDepartment, filterType, filterDateRange]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (selectedProduct && selectedType === "saida") {
      fetchDepartmentsWithStock(selectedProduct);
    }
  }, [selectedProduct, selectedType]);

  useEffect(() => {
    if (selectedDepartment && selectedType === "entrada") {
      checkDepartmentProducts(selectedDepartment);
    }
  }, [selectedDepartment, selectedType]);

  const fetchMovements = async () => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          products (
            name,
            code
          ),
          departments (
            name,
            sigla
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedMovements = data?.map(item => ({
        id: item.id,
        created_at: item.created_at,
        type: item.type,
        quantity: item.quantity,
        responsible: item.responsible,
        product_id: item.product_id,
        product_name: item.products?.name || '',
        product_code: item.products?.code || '',
        department_id: item.department_id,
        department_name: item.departments?.name || '',
        department_sigla: item.departments?.sigla || ''
      })) || [];

      setMovements(formattedMovements);

      // Calcular estatísticas do último mês
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      const recentMovements = formattedMovements.filter(m => new Date(m.created_at) >= lastMonth);

      setStats({
        entrances: recentMovements.filter(m => m.type === "entrada")
          .reduce((sum, m) => sum + m.quantity, 0),
        exits: recentMovements.filter(m => m.type === "saida")
          .reduce((sum, m) => sum + m.quantity, 0)
      });
    } catch (error) {
      console.error("Erro ao buscar movimentações:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as movimentações",
        variant: "destructive",
      });
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("id, name")
        .order("name");

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as categorias",
        variant: "destructive",
      });
    }
  };

  const fetchProducts = async (categoryId?: string) => {
    try {
      let query = supabase
        .from("products")
        .select("id, name, code, category_id")
        .order("name");

      if (categoryId) {
        query = query.eq("category_id", categoryId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setProducts(data || []);
      setSelectedProduct(""); // Limpa o produto selecionado quando mudar a categoria
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from("departments")
        .select("id, name, sigla")
        .order("sigla");

      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error("Erro ao buscar departamentos:", error);
    }
  };

  const fetchDepartmentsWithStock = async (productId: string) => {
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select(`
          quantity,
          departments (
            id,
            name,
            sigla
          )
        `)
        .eq('product_id', productId)
        .gt('quantity', 0);

      if (error) throw error;

      const departments = data
        ?.filter(item => item.departments && item.quantity > 0)
        .map(item => ({
          id: item.departments.id,
          name: item.departments.name,
          sigla: item.departments.sigla,
          quantity: item.quantity
        })) || [];

      setAvailableDepartments(departments);

      // Se não houver departamentos disponíveis, mostrar mensagem
      if (departments.length === 0) {
        toast({
          title: "Aviso",
          description: "Não há secretarias com estoque disponível para este produto",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Erro ao buscar departamentos com estoque:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as secretarias disponíveis",
        variant: "destructive",
      });
    }
  };

  const handleTypeChange = async (type: "entrada" | "saida") => {
    setSelectedType(type);
    setSelectedDepartment("");
    setSelectedCategory("");
    setSelectedProduct("");
    setQuantity("");

    if (type === "saida") {
      // Buscar secretarias que têm produtos em estoque
      const { data: deptsWithStock } = await supabase
        .from('inventory')
        .select('department_id')
        .gt('quantity', 0);

      if (deptsWithStock) {
        const uniqueDeptIds = [...new Set(deptsWithStock.map(item => item.department_id))];
        const availableDepts = departments.filter(dept => uniqueDeptIds.includes(dept.id));
        setAvailableDepartments(availableDepts);
      }
    }
  };

  const handleDepartmentChange = async (departmentId: string) => {
    setSelectedDepartment(departmentId);
    setSelectedCategory("");
    setSelectedProduct("");
    setQuantity("");

    // Carregar categorias
    const { data: categoriesData } = await supabase
      .from('categories')
      .select('*')
      .order('name');

    if (categoriesData) {
      setCategories(categoriesData);
    }
  };

  const handleCategoryChange = async (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedProduct("");
    setQuantity("");

    // Carregar produtos da categoria selecionada
    const { data: productsData } = await supabase
      .from('products')
      .select('*')
      .eq('category_id', categoryId)
      .order('name');

    if (productsData) {
      setProducts(productsData);
    }
  };

  const handleProductChange = (productId: string) => {
    setSelectedProduct(productId);
    setQuantity("");
  };

  const handleSubmit = async () => {
    // Validar campos obrigatórios
    if (!selectedType || !selectedDepartment || !selectedProduct || !quantity || !responsible) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos",
        variant: "destructive",
      });
      return;
    }

    // Validação específica para saída
    if (selectedType === "saida") {
      const departmentWithStock = availableDepartments.find(
        d => d.id.toString() === selectedDepartment
      );
      
      if (!departmentWithStock) {
        toast({
          title: "Erro",
          description: "Secretaria selecionada não possui estoque disponível",
          variant: "destructive",
        });
        return;
      }

      if (Number(quantity) > departmentWithStock.quantity) {
        toast({
          title: "Erro",
          description: `Quantidade solicitada (${quantity}) maior que o estoque disponível (${departmentWithStock.quantity})`,
          variant: "destructive",
        });
        return;
      }
    }

    try {
      setIsSubmitting(true);

      // Primeiro, vamos verificar o estoque atual
      const { data: currentStock, error: stockError } = await supabase
        .from('inventory')
        .select('quantity')
        .eq('product_id', selectedProduct)
        .eq('department_id', selectedDepartment)
        .single();

      if (stockError && stockError.code !== 'PGRST116') { // Ignora erro quando não encontra registro
        throw stockError;
      }

      const currentQuantity = currentStock?.quantity || 0;
      const newQuantity = selectedType === "entrada" 
        ? currentQuantity + Number(quantity)
        : currentQuantity - Number(quantity);

      if (selectedType === "saida" && newQuantity < 0) {
        toast({
          title: "Erro",
          description: "Estoque insuficiente para realizar a saída",
          variant: "destructive",
        });
        return;
      }

      // Criar a movimentação
      const { error: movementError } = await supabase
        .from('transactions')
        .insert([
          {
            type: selectedType,
            product_id: Number(selectedProduct),
            department_id: Number(selectedDepartment),
            quantity: Number(quantity),
            responsible: responsible,
          }
        ]);

      if (movementError) throw movementError;

      // Atualizar o estoque
      const { error: inventoryError } = await supabase
        .from('inventory')
        .upsert(
          {
            product_id: Number(selectedProduct),
            department_id: Number(selectedDepartment),
            quantity: newQuantity
          },
          {
            onConflict: 'product_id,department_id'
          }
        );

      if (inventoryError) throw inventoryError;

      toast({
        title: "Sucesso",
        description: "Movimentação registrada com sucesso",
      });

      // Resetar form e fechar modal
      resetForm();
      setIsOpen(false);
      
      // Recarregar dados
      fetchMovements();
      
    } catch (error) {
      console.error("Erro ao salvar movimentação:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar a movimentação",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedType("");
    setSelectedDepartment("");
    setSelectedProduct("");
    setSelectedCategory("");
    setQuantity("");
    setResponsible("");
    setHasProducts(false);
    setAvailableDepartments([]);
  };

  const checkDepartmentProducts = async (departmentId: string) => {
    try {
      const { count, error } = await supabase
        .from('inventory')
        .select('*', { count: 'exact', head: true })
        .eq('department_id', departmentId);

      if (error) throw error;
      
      setHasProducts(count ? count > 0 : false);
      
      if (!count || count === 0) {
        toast({
          title: "Aviso",
          description: "Esta secretaria não possui produtos cadastrados. Cadastre produtos primeiro antes de realizar uma entrada.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Erro ao verificar produtos do departamento:", error);
      setHasProducts(false);
    }
  };

  const isWithinDateRange = (date: string) => {
    if (filterDateRange === "all") return true;
    
    const movementDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (filterDateRange) {
      case "today":
        return movementDate >= today;
      case "week": {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        weekAgo.setHours(0, 0, 0, 0);
        return movementDate >= weekAgo;
      }
      case "month": {
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        monthAgo.setHours(0, 0, 0, 0);
        return movementDate >= monthAgo;
      }
      default:
        return true;
    }
  };

  const filteredMovements = movements.filter((movement) => {
    // Verificar se os campos necessários existem
    if (!movement || !movement.product_name || !movement.product_code || !movement.responsible) {
      return false;
    }

    const matchesSearch = searchTerm
      ? (
          movement.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          movement.product_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          movement.responsible.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : true;

    const matchesDepartment = filterDepartment === "all" || 
      movement.department_id.toString() === filterDepartment;

    const matchesType = filterType === "all" || movement.type === filterType;

    const matchesDate = isWithinDateRange(movement.created_at);

    return matchesSearch && matchesDepartment && matchesType && matchesDate;
  });

  // Calcular o total de páginas
  const totalPages = Math.ceil(filteredMovements.length / itemsPerPage);

  // Obter os itens da página atual
  const paginatedMovements = filteredMovements.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const renderFilters = () => (
    <div className="flex flex-col sm:flex-row gap-4 w-full">
      {/* Busca */}
      <div className="relative w-full sm:w-[300px]">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input
          type="text"
          placeholder="Buscar por produto, código ou responsável"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap sm:flex-nowrap gap-4">
        <Select value={filterDepartment} onValueChange={setFilterDepartment}>
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Secretaria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {departments.map((department) => (
              <SelectItem 
                key={department.id} 
                value={department.id.toString()}
              >
                {department.sigla}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="entrada">Entradas</SelectItem>
            <SelectItem value="saida">Saídas</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterDateRange} onValueChange={setFilterDateRange}>
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo período</SelectItem>
            <SelectItem value="today">Hoje</SelectItem>
            <SelectItem value="week">Última semana</SelectItem>
            <SelectItem value="month">Último mês</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto space-y-6 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Movimentações</h1>
          <p className="text-muted-foreground">Gerencie as entradas e saídas do estoque</p>
        </div>
        <Button onClick={() => setIsOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nova Movimentação
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-green-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-700">
              Entradas no último mês
            </CardTitle>
            <ArrowUpCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{stats.entrances}</div>
            <p className="text-xs text-green-600">produtos no último mês</p>
          </CardContent>
        </Card>

        <Card className="border-red-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-red-700">
              Saídas no último mês
            </CardTitle>
            <ArrowDownCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{stats.exits}</div>
            <p className="text-xs text-red-600">produtos no último mês</p>
          </CardContent>
        </Card>
      </div>

      <div className="w-full">
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            {renderFilters()}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-medium">Data</th>
                    <th className="px-4 py-3 text-left font-medium">Tipo</th>
                    <th className="px-4 py-3 text-left font-medium">Produto</th>
                    <th className="px-4 py-3 text-center font-medium">Código</th>
                    <th className="px-4 py-3 text-center font-medium">Qtd.</th>
                    <th className="px-4 py-3 text-left font-medium">Responsável</th>
                    <th className="px-4 py-3 text-center font-medium">Secretaria</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMovements.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        Nenhuma movimentação encontrada
                      </td>
                    </tr>
                  ) : (
                    paginatedMovements.map((movement) => (
                      <tr key={movement.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          {new Date(movement.created_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            movement.type === 'entrada' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {movement.type === 'entrada' ? (
                              <>
                                <ArrowUpCircle className="w-3 h-3 mr-1" />
                                Entrada
                              </>
                            ) : (
                              <>
                                <ArrowDownCircle className="w-3 h-3 mr-1" />
                                Saída
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3">{movement.product_name}</td>
                        <td className="px-4 py-3 text-center font-mono text-sm">
                          {movement.product_code}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {movement.quantity}
                        </td>
                        <td className="px-4 py-3">{movement.responsible}</td>
                        <td className="px-4 py-3 text-center">
                          <span 
                            title={movement.department_name}
                            className="cursor-help inline-flex items-center px-2 py-0.5 rounded text-xs"
                          >
                            {movement.department_sigla}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Página {currentPage} de {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[425px] w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Movimentação</DialogTitle>
            <DialogDescription>
              Preencha os dados para registrar uma nova movimentação no estoque
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label>Tipo de Movimentação</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => handleTypeChange("entrada")}
                  variant={selectedType === "entrada" ? "default" : "outline"}
                  className={selectedType === "entrada" ? "bg-green-600 hover:bg-green-700" : "hover:bg-green-100"}
                >
                  <ArrowUpCircle className="w-4 h-4 mr-2" />
                  Entrada
                </Button>
                <Button
                  type="button"
                  onClick={() => handleTypeChange("saida")}
                  variant={selectedType === "saida" ? "default" : "outline"}
                  className={selectedType === "saida" ? "bg-red-600 hover:bg-red-700" : "hover:bg-red-100"}
                >
                  <ArrowDownCircle className="w-4 h-4 mr-2" />
                  Saída
                </Button>
              </div>
            </div>

            {selectedType && (
              <div className="grid gap-2">
                <label>Secretaria</label>
                <Select
                  value={selectedDepartment}
                  onValueChange={handleDepartmentChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma secretaria" />
                  </SelectTrigger>
                  <SelectContent>
                    {(selectedType === "saida" ? availableDepartments : departments).map((department) => (
                      <SelectItem 
                        key={department.id} 
                        value={department.id.toString()}
                        title={department.name}
                      >
                        {department.sigla} - {department.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedType && selectedDepartment && (
              <div className="grid gap-2">
                <label>Categoria</label>
                <Select
                  value={selectedCategory}
                  onValueChange={handleCategoryChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem 
                        key={category.id} 
                        value={category.id.toString()}
                      >
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedType && selectedDepartment && selectedCategory && (
              <div className="grid gap-2">
                <label>Produto</label>
                <Select
                  value={selectedProduct}
                  onValueChange={handleProductChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem 
                        key={product.id} 
                        value={product.id.toString()}
                      >
                        {product.name} - {product.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedProduct && (
              <>
                <div className="grid gap-2">
                  <label>Quantidade</label>
                  <Input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    min="1"
                  />
                </div>

                <div className="grid gap-2">
                  <label>Responsável</label>
                  <Input
                    value={responsible}
                    onChange={(e) => setResponsible(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSubmit} className="w-full sm:w-auto" disabled={isSubmitting}>Salvar Movimentação</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Movements;
